import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/landing/Navbar";
import Footer from "@/components/landing/Footer";
import SubPageHero from "@/components/landing/SubPageHero";
import InvestorAccessModal from "@/components/landing/InvestorAccessModal";
import { LANDING } from "@/constants/testIds";

const ASSETS = [
  {
    key: "monogram-cream",
    label: "KR Monogram · Cream/Gold",
    file: "/brand/kr-monogram-cream.png",
    note: "Primary monogram for dark backgrounds.",
  },
  {
    key: "monogram-dark",
    label: "KR Monogram · Dark",
    file: "/brand/kr-monogram.png",
    note: "Monogram for cream / light backgrounds.",
  },
  {
    key: "logo-full-cream",
    label: "Full Logo · Cream/Gold",
    file: "/brand/kr-logo-cream-full.png",
    note: "Full wordmark for dark backgrounds.",
  },
  {
    key: "logo-full-dark",
    label: "Full Logo · Dark",
    file: "/brand/kr-logo-full.png",
    note: "Full wordmark for cream / light backgrounds.",
  },
];

const FACTS = [
  {
    key: "firm-name",
    label: "Firm",
    value: "Kellermann Reid & Co.",
  },
  {
    key: "form",
    label: "Form",
    value: "Senior partnership · in strategic formation",
  },
  {
    key: "headquarters",
    label: "Headquarters",
    value: "Austin, Texas, United States",
  },
  {
    key: "satellite-offices",
    label: "Coverage Locations",
    value: "New York · London · Geneva · Dubai · Singapore · Hong Kong",
  },
  {
    key: "founders",
    label: "Co-Founders",
    value: "Marcel Kellermann · Chris Reid",
  },
  {
    key: "fund-platform",
    label: "Cayman Fund Platform",
    value: "CV5 Capital (CV5 SPC) · engagement in formation",
  },
  {
    key: "governance",
    label: "Cayman Governance Provider",
    value: "Bell Rock Group · engagement in formation",
  },
  {
    key: "vehicle-architecture",
    label: "Intended Vehicle Architecture",
    value: "Nine vehicles across four structures · working titles",
  },
  {
    key: "mandate",
    label: "Mandate",
    value:
      "Industrial credit · Bridge & special situations · Opportunistic industrial equity · Structured capital",
  },
];

const BOILERPLATE = `Kellermann Reid & Co. is a private markets platform in strategic formation, focused on disciplined, asset-backed industrial credit and opportunistic industrial equity. Founded by Marcel Kellermann and Chris Reid, with Rachel Yang as Partner for Asia-Pacific, the partnership is building an institutional vehicle architecture across nine vehicles and four structures, deployed through a documented Investment Committee and a confidentially curated network of senior industrial operators. The platform is headquartered in Austin, Texas, with active coverage across New York, London, Geneva, Dubai, Singapore and Hong Kong.`;

const PressKitPage = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const openModal = useCallback(() => setModalOpen(true), []);
  const closeModal = useCallback(() => setModalOpen(false), []);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const copyBoilerplate = () => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(BOILERPLATE).catch(() => {});
    }
  };

  return (
    <>
      <Navbar onRequestAccess={openModal} />
      <main
        data-testid={LANDING.pressKitPage}
        className="relative bg-[var(--kr-bg)]"
      >
        <SubPageHero
          index="Press Kit"
          label="Brand assets · Boilerplate · Facts"
          title={
            <>
              For{" "}
              <span className="italic text-[var(--kr-gold)]">media</span> and
              counterparties.
            </>
          }
          description="Brand assets, firm facts and a short boilerplate for media, conference programmes and counterparty introductions. For interview requests and bespoke materials, please contact the partnership directly."
          breadcrumb="Press Kit"
        />

        {/* Boilerplate */}
        <section className="py-20 lg:py-24">
          <div className="max-w-[1480px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-12">
            <div className="lg:col-span-4">
              <div className="flex items-center gap-3 mb-6">
                <span className="font-mono-label text-[10px] text-[var(--kr-gold)]">
                  Boilerplate
                </span>
                <span className="w-10 h-px bg-[var(--kr-gold)]/60" />
              </div>
              <h2 className="font-serif-display text-[var(--kr-text)] text-[clamp(1.7rem,2.9vw,2.3rem)] leading-[1.15] mb-4">
                One paragraph,{" "}
                <span className="italic text-[var(--kr-gold)]">verbatim</span>.
              </h2>
              <p className="text-[var(--kr-text-muted)] text-[14px] leading-[1.7] max-w-[34ch]">
                Use the boilerplate as printed. For tailored language, write to
                the partnership.
              </p>
            </div>
            <div className="lg:col-span-8 lg:pl-8">
              <div className="border border-[var(--kr-line)] p-8 lg:p-10 bg-[var(--kr-bg-2)]">
                <p className="text-[var(--kr-text)] text-[15.5px] leading-[1.95] font-serif-display italic">
                  {BOILERPLATE}
                </p>
                <div className="mt-8 flex flex-wrap items-center gap-4">
                  <button
                    type="button"
                    onClick={copyBoilerplate}
                    className="kr-btn-ghost"
                  >
                    Copy boilerplate →
                  </button>
                  <span className="font-mono-label text-[10px] text-[var(--kr-text-muted)]">
                    {BOILERPLATE.split(" ").length} words
                  </span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Assets */}
        <section className="py-20 lg:py-24 border-t border-[var(--kr-line)] bg-[var(--kr-bg-2)]">
          <div className="max-w-[1480px] mx-auto px-6 lg:px-12">
            <div className="flex items-center gap-3 mb-12">
              <span className="font-mono-label text-[10px] text-[var(--kr-gold)]">
                Brand Assets
              </span>
              <span className="w-10 h-px bg-[var(--kr-gold)]/60" />
              <span className="font-mono-label text-[10px] text-[var(--kr-text-muted)]">
                Right-click → Save Image As
              </span>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-0 border-t border-l border-[var(--kr-line)]">
              {ASSETS.map((a) => (
                <article
                  key={a.key}
                  data-testid={`${LANDING.pressKitAssetPrefix}${a.key}`}
                  className="border-r border-b border-[var(--kr-line)] flex flex-col"
                >
                  <div
                    className={`h-44 flex items-center justify-center p-6 ${
                      a.key.includes("cream")
                        ? ""
                        : "bg-[#f5efe2]"
                    }`}
                  >
                    <img
                      src={a.file}
                      alt={a.label}
                      className="max-h-full max-w-full object-contain"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-6 border-t border-[var(--kr-line)] flex-1 flex flex-col">
                    <h3 className="font-serif-display text-[1.05rem] text-[var(--kr-text)] leading-[1.25] mb-2">
                      {a.label}
                    </h3>
                    <p className="text-[var(--kr-text-muted)] text-[12.5px] leading-[1.65] mb-4 flex-1">
                      {a.note}
                    </p>
                    <a
                      href={a.file}
                      download
                      className="kr-link font-mono-label text-[10px]"
                    >
                      Download PNG →
                    </a>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* Facts */}
        <section className="py-20 lg:py-28 border-t border-[var(--kr-line)]">
          <div className="max-w-[1480px] mx-auto px-6 lg:px-12">
            <div className="flex items-center gap-3 mb-12">
              <span className="font-mono-label text-[10px] text-[var(--kr-gold)]">
                Firm Facts
              </span>
              <span className="w-10 h-px bg-[var(--kr-gold)]/60" />
            </div>
            <dl className="grid md:grid-cols-2 gap-0 border-t border-l border-[var(--kr-line)]">
              {FACTS.map((f) => (
                <div
                  key={f.key}
                  data-testid={`${LANDING.pressKitFactPrefix}${f.key}`}
                  className="p-8 border-r border-b border-[var(--kr-line)]"
                >
                  <dt className="font-mono-label text-[10px] text-[var(--kr-text-muted)] mb-3">
                    {f.label}
                  </dt>
                  <dd className="font-serif-display text-[1.05rem] text-[var(--kr-text)] leading-[1.45]">
                    {f.value}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        </section>

        {/* CTA */}
        <section className="border-t border-[var(--kr-line)] py-20 bg-[var(--kr-bg-2)]">
          <div className="max-w-[1480px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <h2 className="font-serif-display text-[var(--kr-text)] text-[clamp(1.7rem,2.8vw,2.3rem)] leading-[1.2] max-w-[40ch]">
                For interviews, tailored language or commentary, write to the{" "}
                <span className="italic text-[var(--kr-gold)]">partnership</span>
                .
              </h2>
            </div>
            <div className="lg:col-span-5 flex flex-wrap gap-4 lg:justify-end">
              <Link
                to="/contact"
                data-testid={LANDING.pressKitContact}
                className="kr-btn-primary"
              >
                Media Enquiries
                <span aria-hidden>→</span>
              </Link>
              <a
                href="mailto:investors@krprivatecapital.com"
                className="kr-btn-ghost"
              >
                investors@krprivatecapital.com
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <InvestorAccessModal open={modalOpen} onClose={closeModal} />
    </>
  );
};

export default PressKitPage;
